import { State, TopicData } from "@/shared/state/visualization";
import { LayoutType, PanelState } from "@/shared/state/visualization/schema";

import {
  getMessagePathNodeFromRecord,
  MessagePathNode,
  MessagePathPartType,
  MessagePathRecord,
  TopicRecord,
} from "../topics";

import { LayoutDefinition } from "./LayoutDefinition";
import { Requirement, LayoutPanelState, Rule, RuleStyle } from "./LayoutRecord";

export class LayoutDefinitionBuilder {
  #panels: Record<string, LayoutPanelState>;
  #layout: State["layout"];
  #rules: Rule[];
  #ruleRegistry: RuleRegistry;
  #schemaVersion: number;

  /**
   * Utility function to create a requirement from a topic data object.
   */
  static createRequirementFromTopicData(topicData: TopicData): Requirement {
    return {
      topicMessagePath: topicData.messagePath.dotPath
        ? `${topicData.topic.name}.${MessagePathNode.partsToString(
            topicData.messagePath.parts,
          )}`
        : topicData.topic.name,
      dataType: [...topicData.messagePath.parts]
        .reverse()
        .find((part) => part.type === MessagePathPartType.Attr)?.dataType,
    };
  }

  static createRequirementFromMessagePathRecord(
    topicRecord: TopicRecord,
    messagePathRecord: MessagePathRecord,
  ): Requirement {
    const [messagePathNode] = getMessagePathNodeFromRecord(
      topicRecord,
      messagePathRecord,
    );

    return {
      topicMessagePath: messagePathNode.toString(),
      dataType: messagePathNode.data.canonical_data_type,
    };
  }

  constructor(layoutDefinition?: LayoutDefinition) {
    if (layoutDefinition) {
      this.#panels = { ...layoutDefinition.state.panels };
      this.#layout = { ...layoutDefinition.state.layout };
      this.#rules = [...layoutDefinition.state.rules];
      this.#ruleRegistry = new RuleRegistry();

      this.#rules.forEach((rule) => {
        this.#ruleRegistry.setRuleId(rule.id, rule.requirements, rule.style);
      });

      this.#schemaVersion = layoutDefinition.schemaVersion;
      return;
    }

    this.#panels = {};
    this.#layout = {
      id: "root",
      axis: "x",
      relativeSize: 1,
      children: [],
    };
    this.#rules = [];
    this.#ruleRegistry = new RuleRegistry();
    this.#schemaVersion = 1;
  }

  /**
   * This method only supports single direction layouts at the moment.
   */
  public addPanelToLayout(panelId: string): this {
    this.#layout.children.push({
      id: panelId,
      type: LayoutType.Panel,
      relativeSize: 1,
      isResizing: false,
    });

    // Update all children sizes to be equal
    const numChildren = this.#layout.children.length;
    const newSize = 1 / numChildren;

    this.#layout.children = this.#layout.children.map((child) => ({
      ...child,
      relativeSize: newSize,
    }));

    return this;
  }

  public addRule(requirements: Requirement[], style?: RuleStyle): string {
    const isNewRule = !this.#ruleRegistry.hasRule(requirements, style);
    const ruleId = this.#ruleRegistry.getRuleId(requirements, style);

    if (isNewRule) {
      this.#rules.push({ id: ruleId, requirements, ...(style && { style }) });
    }

    return ruleId;
  }

  public addPanel(panel: Omit<PanelState, "data">, rules: string[]): this {
    const uniqueRules = new Set(rules);

    this.#panels[panel.id] = {
      ...panel,
      rules: Array.from(uniqueRules),
    };

    return this;
  }

  public setLayout(layout: State["layout"]): this {
    this.#layout = layout;
    return this;
  }

  /**
   * Removes a panel from the layout definition
   */
  public removePanel(panelId: string): this {
    if (this.#panels[panelId]) {
      delete this.#panels[panelId];
    }

    this.#cleanupUnusedRules();

    return this;
  }

  /**
   * Updates an existing panel with new metadata and requirements
   */
  public updatePanel(
    panelId: string,
    panelMetadata: Omit<PanelState, "data">,
    rules: string[],
  ): this {
    const uniqueRules = new Set(rules);

    // If the panel doesn't exist, add it
    if (!this.#panels[panelId]) {
      return this.addPanel(panelMetadata, Array.from(uniqueRules));
    }

    this.#panels[panelId] = {
      ...panelMetadata,
      id: panelId,
      rules: Array.from(uniqueRules),
    };

    return this;
  }

  public build(): LayoutDefinition {
    this.#cleanupUnusedRules();

    const layoutState = {
      panels: this.#panels,
      layout: this.#layout,
      rules: this.#rules,
    };

    return new LayoutDefinition(layoutState, this.#schemaVersion);
  }

  /**
   * Removes rules that are no longer used by any panel
   */
  #cleanupUnusedRules(): void {
    // Collect all rule IDs used by panels
    const usedRuleIds = new Set<string>();
    Object.values(this.#panels).forEach((panel) => {
      panel.rules.forEach((ruleId) => usedRuleIds.add(ruleId));
    });

    // Filter out rules that are no longer used
    this.#rules = this.#rules.filter((rule) => usedRuleIds.has(rule.id));
  }
}

class RuleRegistry {
  #signatureToRuleId = new Map<string, string>();

  public getRuleId(requirements: Requirement[], style?: RuleStyle): string {
    const signature = this.#createRuleSignature(requirements, style);

    if (!this.#signatureToRuleId.has(signature)) {
      this.#signatureToRuleId.set(
        signature,
        `rule$${crypto.randomUUID().substring(0, 8)}`,
      );
    }

    return this.#signatureToRuleId.get(signature)!;
  }

  public hasRule(requirements: Requirement[], style?: RuleStyle): boolean {
    return this.#signatureToRuleId.has(
      this.#createRuleSignature(requirements, style),
    );
  }

  public removeRule(requirements: Requirement[], style?: RuleStyle): void {
    const signature = this.#createRuleSignature(requirements, style);
    this.#signatureToRuleId.delete(signature);
  }

  public setRuleId(
    ruleId: string,
    requirements: Requirement[],
    style?: RuleStyle,
  ): void {
    this.#signatureToRuleId.set(
      this.#createRuleSignature(requirements, style),
      ruleId,
    );
  }

  #createRuleSignature(requirements: Requirement[], style?: RuleStyle): string {
    const requirementSignature = requirements
      .map(
        (requirement) =>
          `${requirement.topicMessagePath}:${requirement.dataType || ""}`,
      )
      .sort()
      .join("|");

    if (!style) {
      return requirementSignature;
    }

    return `${requirementSignature}:${JSON.stringify(style)}`;
  }
}
